import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface AnnouncerValue {
  announce(message: string): void;
}

const AnnouncerContext = createContext<AnnouncerValue | null>(null);

export function AnnouncerProvider({ children }: PropsWithChildren) {
  const [message, setMessage] = useState('');
  const announceTimer = useRef<number | undefined>(undefined);
  const clearTimer = useRef<number | undefined>(undefined);

  const announce = useCallback((nextMessage: string) => {
    window.clearTimeout(announceTimer.current);
    window.clearTimeout(clearTimer.current);
    setMessage('');
    announceTimer.current = window.setTimeout(() => {
      setMessage(nextMessage);
      clearTimer.current = window.setTimeout(() => setMessage(''), 10_000);
    }, 50);
  }, []);

  useEffect(() => () => {
    window.clearTimeout(announceTimer.current);
    window.clearTimeout(clearTimer.current);
  }, []);

  const value = useMemo(() => ({ announce }), [announce]);

  return (
    <AnnouncerContext.Provider value={value}>
      {children}
      <div className="visually-hidden" role="status" aria-live="polite" aria-atomic="true" aria-label="Estado">
        {message}
      </div>
    </AnnouncerContext.Provider>
  );
}

export function useAnnouncer(): AnnouncerValue {
  const context = useContext(AnnouncerContext);
  if (!context) throw new Error('useAnnouncer debe utilizarse dentro de AnnouncerProvider.');
  return context;
}
